import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Grid } from "@mui/material";
import OfferServices from "../../../services/OfferService";
import OfferHeader from "./OfferDetails/OfferHeader";
import OfferInfoSection from "./OfferDetails/OfferInfoSection";
import OfferItemsSection from "./OfferDetails/OfferItemsSection";
import OfferSummarySection from "./OfferDetails/OfferSummarySection";

const OfferDetailsScreen = ({ offerId, onBack }) => {
  const [offerDetails, setOfferDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch offer details
  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        setLoading(true);
        const response = await OfferServices.getOfferDetailById(offerId);
        if (response?.data?.view) {
          setOfferDetails(response.data.view);
        }
      } catch (error) {
        console.error("Error fetching offer details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (offerId) {
      fetchOfferDetails();
    }
  }, [offerId]);

  // Handle actions
  const handlePrint = () => {
    if (!offerDetails) return;

    const formatDate = (d) =>
      d
        ? new Date(d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        : "Not specified";

    const itemsHtml = (offerDetails.OfferItems || [])
      .map((offerItem) => {
        const item = offerItem.MenuItem;
        if (!item) return "";
        return `
        <tr>
          <td style="padding:10px 8px;border-bottom:1px solid #f0f0f0;">
            <span style="font-weight:600;color:#1a1a2e;">${item.name}</span>
            ${item.description ? `<br/><span style="font-size:11px;color:#888;">${item.description}</span>` : ""}
          </td>
          <td style="padding:10px 8px;border-bottom:1px solid #f0f0f0;text-align:center;">
            <span style="background:#f0f4ff;color:#4361ee;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600;">
              Qty: ${offerItem.quantity || 1}
            </span>
          </td>
          <td style="padding:10px 8px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600;color:#1a1a2e;">
            Rs. ${parseFloat(item.basePrice || item.price || 0).toFixed(2)}
          </td>
        </tr>`;
      })
      .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Offer - ${offerDetails.name}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#333; }
    @page { size:A4; margin:20mm 15mm; }
    .page { max-width:760px; margin:0 auto; padding:0; }

    /* Header */
    .header { background:linear-gradient(135deg,#e63946 0%,#c1121f 100%); color:#fff; padding:28px 32px; border-radius:0 0 24px 24px; margin-bottom:28px; }
    .header-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
    .brand { font-size:13px; font-weight:700; letter-spacing:2px; text-transform:uppercase; opacity:.85; }
    .badge { background:rgba(255,255,255,.2); border:1px solid rgba(255,255,255,.4); padding:4px 14px; border-radius:20px; font-size:12px; font-weight:700; letter-spacing:1px; }
    .badge.inactive { background:rgba(0,0,0,.2); }
    .offer-title { font-size:26px; font-weight:800; letter-spacing:-.5px; margin-bottom:6px; }
    .offer-subtitle { font-size:13px; opacity:.85; }
    .offer-type { display:inline-block; background:rgba(255,255,255,.25); padding:3px 12px; border-radius:12px; font-size:11px; font-weight:700; letter-spacing:1px; text-transform:uppercase; margin-top:8px; }

    /* Price highlight */
    .price-card { background:linear-gradient(135deg,#fff5f5 0%,#fff 100%); border:2px solid #e63946; border-radius:16px; padding:20px 28px; margin-bottom:24px; display:flex; align-items:center; justify-content:space-between; }
    .price-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#888; margin-bottom:4px; }
    .price-value { font-size:36px; font-weight:900; color:#e63946; line-height:1; }
    .price-note { font-size:12px; color:#888; margin-top:4px; }
    .discount-badge { background:#e63946; color:#fff; padding:6px 16px; border-radius:20px; font-size:13px; font-weight:800; }

    /* Section */
    .section { margin-bottom:22px; }
    .section-title { font-size:14px; font-weight:800; text-transform:uppercase; letter-spacing:1px; color:#1a1a2e; margin-bottom:12px; padding-bottom:8px; border-bottom:2px solid #e63946; display:flex; align-items:center; gap:6px; }
    .section-title::before { content:''; display:inline-block; width:4px; height:16px; background:#e63946; border-radius:2px; }

    /* Info grid */
    .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
    .info-item { background:#f8fafc; border-radius:10px; padding:12px 16px; }
    .info-item-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#aaa; margin-bottom:4px; }
    .info-item-value { font-size:14px; font-weight:700; color:#1a1a2e; }

    /* Items table */
    table { width:100%; border-collapse:collapse; }
    thead tr { background:#1a1a2e; }
    thead th { padding:10px 8px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.8px; color:#fff; }
    thead th:last-child { text-align:right; }
    thead th:nth-child(2) { text-align:center; }
    tbody tr:last-child td { border-bottom:none; }

    /* Description */
    .description-box { background:#f8fafc; border-left:4px solid #e63946; border-radius:0 10px 10px 0; padding:14px 18px; font-size:13px; line-height:1.7; color:#555; }

    /* Footer */
    .footer { margin-top:32px; padding-top:16px; border-top:1px solid #eee; display:flex; align-items:center; justify-content:space-between; font-size:11px; color:#bbb; }
  </style>
</head>
<body>
<div class="page">
  <!-- Header -->
  <div class="header">
    <div class="header-top">
      <span class="brand">Special Offer</span>
      <span class="badge ${offerDetails.isActive ? "" : "inactive"}">${offerDetails.isActive ? "ACTIVE" : "INACTIVE"}</span>
    </div>
    <div class="offer-title">${offerDetails.name}</div>
    <div class="offer-subtitle">${offerDetails.description || ""}</div>
    <span class="offer-type">${offerDetails.type}</span>
  </div>

  <!-- Price -->
  <div class="price-card">
    <div>
      <div class="price-label">Offer Price</div>
      <div class="price-value">Rs. ${parseFloat(offerDetails.price).toFixed(2)}</div>
      ${offerDetails.originalPrice ? `<div class="price-note">Original: Rs. ${parseFloat(offerDetails.originalPrice).toFixed(2)}</div>` : ""}
    </div>
    ${offerDetails.discountPercentage
        ? `<div class="discount-badge">${offerDetails.discountPercentage}% OFF</div>`
        : offerDetails.discountValue
          ? `<div class="discount-badge">Rs. ${parseFloat(offerDetails.discountValue).toFixed(2)} OFF</div>`
          : ""
      }
  </div>

  <!-- Offer Info -->
  <div class="section">
    <div class="section-title">Offer Information</div>
    <div class="info-grid">
      ${offerDetails.Category ? `<div class="info-item"><div class="info-item-label">Category</div><div class="info-item-value">${offerDetails.Category.name}</div></div>` : ""}
      ${offerDetails.SubCategory ? `<div class="info-item"><div class="info-item-label">Subcategory</div><div class="info-item-value">${offerDetails.SubCategory.name}</div></div>` : ""}
      ${offerDetails.startDate ? `<div class="info-item"><div class="info-item-label">Start Date</div><div class="info-item-value">${formatDate(offerDetails.startDate)}</div></div>` : ""}
      ${offerDetails.endDate ? `<div class="info-item"><div class="info-item-label">End Date</div><div class="info-item-value">${formatDate(offerDetails.endDate)}</div></div>` : ""}
    </div>
  </div>

  ${offerDetails.OfferItems && offerDetails.OfferItems.length > 0
        ? `<div class="section">
    <div class="section-title">Included Items (${offerDetails.OfferItems.length})</div>
    <table>
      <thead><tr>
        <th>Item</th>
        <th style="text-align:center;">Quantity</th>
        <th style="text-align:right;">Unit Price</th>
      </tr></thead>
      <tbody>${itemsHtml}</tbody>
    </table>
  </div>`
        : ""
      }

  ${offerDetails.description
        ? `<div class="section">
    <div class="section-title">Description</div>
    <div class="description-box">${offerDetails.description}</div>
  </div>`
        : ""
      }

  <div class="footer">
    <span>Printed on ${new Date().toLocaleString()}</span>
    <span>Offer ID: ${offerDetails.id || ""}</span>
  </div>
</div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 500);
  };

  const handleShare = () => {
    console.log("Share offer:", offerId);
    // Implement share functionality
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Loading offer details...
        </Typography>
      </Box>
    );
  }

  if (!offerDetails) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          gap: 3,
        }}
      >
        <Typography
          variant="h4"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Offer not found
        </Typography>
        <Button
          variant="contained"
          onClick={onBack}
          size="large"
          sx={{
            height: 56,
            px: 4,
            borderRadius: "16px",
            fontSize: "1.1rem",
            fontWeight: 600,
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafc",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <OfferHeader
        offerDetails={offerDetails}
        onBack={onBack}
        onPrint={handlePrint}
      />

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
        }}
      >
        <Grid container spacing={1.5}>
          {/* Left Column - Offer Info & Items */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Offer Information */}
              <OfferInfoSection offerDetails={offerDetails} />

              {/* Offer Items */}
              {offerDetails.OfferItems &&
                offerDetails.OfferItems.length > 0 && (
                  <OfferItemsSection offerDetails={offerDetails} />
                )}
            </Box>
          </Grid>

          {/* Right Column - Summary */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Price Summary */}
              <OfferSummarySection
                offerDetails={offerDetails}
                onPrint={handlePrint}
                onShare={handleShare}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OfferDetailsScreen;
